/**
 * RegistroIngresos.js — Lista de Registros de Ingresos
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { ConfirmModal } from "../../../../shared/components/SharedUI";
import invoiceStyles from "../../invoices/styles/invoice.module.css";

// ── helpers ──────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

// ── Vista lista ───────────────────────────────────────────────────────────────
function ListaIngresos({ onNuevo }) {
  const { t } = useTranslation();
  const [items,     setItems]     = useState([]);
  const [count,     setCount]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [search,    setSearch]    = useState("");
  const [loading,   setLoading]   = useState(true);
  const [deleting,  setDeleting]  = useState(null);
  const [delLoad,   setDelLoad]   = useState(false);
  const [msg,       setMsg]       = useState(null);
  const [detalle,   setDetalle]   = useState(null);

  const PAGE_SIZE  = 20;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const fetchData = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ page, page_size: PAGE_SIZE, tipo: "INGRESO" });
    if (search.trim()) qs.set("search", search.trim());
    apiFetch(`/api/finance/asientos/?${qs}`)
      .then(r => r.json())
      .then(d => { setItems(d.results ?? []); setCount(d.count ?? 0); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function showMsg(ok, text) {
    setMsg({ ok, text });
    setTimeout(() => setMsg(null), 4000);
  }

  async function handleDelete() {
    if (!deleting) return;
    setDelLoad(true);
    try {
      const res = await apiFetch(`/api/finance/asientos/${deleting.id}/`, { method: "DELETE" });
      if (!res.ok) throw new Error("Error al eliminar");
      setDeleting(null);
      showMsg(true, `Ingreso ${deleting.numero} eliminado correctamente.`);
      fetchData();
    } catch (e) { showMsg(false, e.message); }
    finally { setDelLoad(false); }
  }

  const Btn = ({ onClick, bg, border, color, icon, title }) => (
    <button type="button" onClick={onClick} title={title}
      style={{ background: bg, border: `1px solid ${border}`,
        borderRadius: 6, padding: "4px 10px", color, cursor: "pointer" }}>
      <i className={`fa-solid ${icon}`}/>
    </button>
  );

  return (
    <>
      {/* Breadcrumb */}
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("registros.ingresos")}</span>
      </span>

      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2"
        style={{ marginBottom: 6 }}>
        <div>
          <h2 className="title mt-2 mb-2">{t("registros.ingresos")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("registros.administraIngresos")}</p>
        </div>
        <button onClick={onNuevo} className="btn"
          style={{ padding: "9px 18px", background: "#f97316", borderRadius: 6,
            color: "#fff", fontWeight: 600, fontSize: 14,
            display: "flex", alignItems: "center", gap: 7, border: "none" }}>
          <i className="fa-solid fa-plus"/> {t("registros.nuevoIngreso")}
        </button>
      </div>

      {/* Toast */}
      {msg && (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: msg.ok ? "rgba(74,222,128,0.07)" : "rgba(239,68,68,0.07)",
          border: `1px solid ${msg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
          borderLeft: `4px solid ${msg.ok ? "#4ade80" : "#f87171"}`,
          borderRadius: 8, padding: "12px 18px", display: "flex", alignItems: "center",
          gap: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.4)", maxWidth: 380 }}>
          <i className={`fa-solid ${msg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}
             style={{ color: msg.ok ? "#4ade80" : "#f87171", fontSize: "1.1rem" }}/>
          <span style={{ color: msg.ok ? "#4ade80" : "#f87171", fontWeight: 600 }}>{msg.text}</span>
        </div>
      )}

      {/* Tabla */}
      <div className={invoiceStyles["table-container"]}>
        <div className={`${invoiceStyles["table-header"]} d-flex justify-content-between`}>
          <div className={`${invoiceStyles["input-header"]} d-flex gap-2`}>
            <i className="fa-solid fa-magnifying-glass"
               style={{ color: loading ? "#f97316" : undefined, transition: "color 0.2s" }}/>
            <input type="text" placeholder={t("registros.buscarPlaceholderIngresos")}
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}/>
            {search && (
              <button onClick={() => { setSearch(""); setPage(1); }}
                style={{ background: "none", border: "none", color: "var(--text-muted)",
                  cursor: "pointer", fontSize: "0.8rem" }}>✕</button>
            )}
          </div>
        </div>

        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th>{t("asientos.numAsiento")}</th>
                <th>{t("registros.detalleReferencia")}</th>
                <th>{t("registros.bancoDebe")}</th>
                <th>{t("registros.cuentaIngresoHaber")}</th>
                <th>{t("common.fecha")}</th>
                <th><div style={{ textAlign: "right" }}>{t("common.monto")}</div></th>
                <th className="text-center">{t("common.acciones")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: "var(--text-secondary)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin"
                     style={{ marginRight: 8, color: "#f97316" }}/>
                  {t("registros.cargandoIngresos")}
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                  <i className="fa-solid fa-inbox" style={{ marginRight: 8, fontSize: "1.2rem" }}/>
                  {t("registros.sinIngresos")}
                </td></tr>
              ) : items.map(item => {
                const monto = Number(item.total_debitos || 0);
                return (
                  <tr key={item.id}>
                    <td style={{ fontFamily: "monospace", color: "#4ade80", fontWeight: 600 }}>
                      {item.numero}
                    </td>
                    <td>
                      <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{item.concepto}</span>
                      {item.referencia && (
                        <span style={{ display: "block", fontSize: "0.75rem",
                          color: "var(--text-secondary)", marginTop: 1 }}>{item.referencia}</span>
                      )}
                    </td>
                    <td style={{ color: "var(--text-primary)", fontSize: 13 }}>
                      {item.cuenta_debe_nombre ?? "—"}
                    </td>
                    <td style={{ color: "var(--text-primary)", fontSize: 13 }}>
                      {item.cuenta_haber_nombre ?? "—"}
                    </td>
                    <td>{fmtDate(item.fecha)}</td>
                    <td>
                      <div style={{ textAlign: "right", fontFamily: "monospace",
                        fontWeight: 600, color: "#4ade80" }}>
                        ₡{formatCurrency(monto)}
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center justify-content-center" style={{ gap: 6 }}>
                        <Btn onClick={() => {
                            apiFetch(`/api/finance/asientos/${item.id}/`)
                              .then(r => r.json())
                              .then(full => setDetalle(full))
                              .catch(() => setDetalle(item));
                          }}
                          bg="rgba(96,165,250,0.1)"  border="rgba(96,165,250,0.3)"
                          color="#60a5fa" icon="fa-eye" title={t("common.ver")}/>
                        <Btn onClick={() => setDeleting(item)}
                          bg="rgba(239,68,68,0.1)" border="rgba(239,68,68,0.25)"
                          color="#f87171" icon="fa-trash" title={t("common.eliminar")}/>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }}/>
                {t("common.cargando")}
              </span>
            ) : (
              <>
                <span>{count}</span> {t("registros.registros")} · {t("common.pagina")}{" "}
                <span>{page}</span> {t("common.de")} <span>{Math.max(1, totalPages)}</span>
              </>
            )}
          </p>
          <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
            <button disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}>
              {t("common.anterior")}
            </button>
            <button disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}>
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>

      {/* Modal detalle */}
      {detalle && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000,
          background: "rgba(0,0,0,0.75)", backdropFilter: "blur(3px)",
          display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setDetalle(null)}>
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 8, width: "min(540px,96vw)", maxHeight: "90vh", overflow: "auto" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border-card)",
              display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600,
                  color: "var(--text-primary)", fontFamily: "monospace" }}>{detalle.numero}</h2>
                <p style={{ margin: "3px 0 0", color: "var(--text-secondary)", fontSize: 13 }}>
                  {fmtDate(detalle.fecha)} · {detalle.concepto}
                </p>
              </div>
              <button onClick={() => setDetalle(null)}
                style={{ background: "none", border: "none", color: "var(--text-secondary)",
                  cursor: "pointer", fontSize: "1.1rem" }}>
                <i className="fa-solid fa-xmark"/>
              </button>
            </div>
            <div style={{ padding: "16px 20px" }}>
              {(detalle.lineas ?? []).map((l, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between",
                  padding: "9px 4px", borderBottom: "1px solid var(--border-card)",
                  background: i % 2 === 0 ? "var(--hover-bg)" : "transparent" }}>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 700, marginRight: 8,
                      color: Number(l.debito) > 0 ? "#f87171" : "#60a5fa" }}>
                      {Number(l.debito) > 0 ? t("asientos.debe").toUpperCase() : t("asientos.haber").toUpperCase()}
                    </span>
                    <span style={{ color: "var(--text-primary)", fontSize: 13 }}>{l.cuenta_nombre}</span>
                    {l.descripcion && (
                      <span style={{ display: "block", color: "var(--text-muted)", fontSize: 11,
                        marginTop: 2, marginLeft: 46 }}>{l.descripcion}</span>
                    )}
                  </div>
                  <span style={{ fontFamily: "monospace", fontWeight: 600, fontSize: 13,
                    color: Number(l.debito) > 0 ? "#f87171" : "#60a5fa" }}>
                    ₡{formatCurrency(Number(l.debito) || Number(l.credito))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!deleting}
        name={deleting?.numero}
        message="Se eliminará el registro de ingreso y su asiento de diario. Esta acción no se puede deshacer."
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
        loading={delLoad}
      />
    </>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function RegistroIngresos() {
  const navigate = useNavigate();
  return <ListaIngresos onNuevo={() => navigate("/finanzas/ingresos/nuevo")} />;
}
